import type { SilencedUser } from '@/mocks/adminModeracion';

interface Props {
  users: SilencedUser[];
  onToggleActive: (id: string) => void;
}

export default function SilencedUsersList({ users, onToggleActive }: Props) {
  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('es-CR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  const active = users.filter((u) => u.active);

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {[
          { label: 'Silenciados activos', value: active.length, icon: 'ri-volume-mute-line', color: 'text-amber-500' },
          { label: 'Vencidos', value: users.filter((u) => !u.active).length, icon: 'ri-check-line', color: 'text-emerald-500' },
          { label: 'Total', value: users.length, icon: 'ri-user-voice-line', color: 'text-foreground-500' },
        ].map((s) => (
          <div key={s.label} className="bg-background-50 border border-background-200 rounded-xl p-3.5">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center">
                <i className={`${s.icon} ${s.color} text-sm`}></i>
              </div>
              <span className="text-[10px] text-foreground-400 font-medium">{s.label}</span>
            </div>
            <p className="text-lg font-bold text-foreground-900">{s.value}</p>
          </div>
        ))}
      </div>

      {/* User cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {users.map((u) => (
          <div key={u.id} className={`bg-background-50 border rounded-xl p-4 transition-all ${u.active ? 'border-amber-200 bg-amber-50/30' : 'border-background-100 opacity-60'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2.5">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
                  u.active ? 'bg-amber-200 text-amber-700' : 'bg-background-200 text-foreground-400'
                }`}>
                  {u.username.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-xs font-bold text-foreground-900">{u.username}</p>
                  <p className="text-[9px] text-foreground-400">{formatDate(u.silencedAt)}</p>
                </div>
              </div>
              <button
                onClick={() => onToggleActive(u.id)}
                className={`px-2.5 py-1 rounded-full text-[10px] font-semibold cursor-pointer transition-all whitespace-nowrap ${
                  u.active ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-background-200 text-foreground-500 hover:bg-background-300'
                }`}
              >
                {u.active ? 'Quitar silencio' : 'Ya vencido'}
              </button>
            </div>
            <div className="bg-background-50 rounded-lg px-3 py-2 mb-2 border border-background-100">
              <p className="text-[10px] text-foreground-600">{u.reason}</p>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-background-100">
              <div className="flex items-center gap-1">
                <i className="ri-timer-line text-foreground-400 text-[10px]"></i>
                <span className={`text-[10px] font-semibold ${u.active ? 'text-amber-600' : 'text-foreground-400'}`}>{u.duration}</span>
              </div>
              <span className="text-[10px] text-foreground-500">{u.silencedBy}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}